import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';

import { AuthProvider } from './auth/AuthProvider';
import { SessionGuard } from './auth/SessionGuard';
import { readEnv } from './env';
import { RootErrorBoundary } from './errors/RootErrorBoundary';
import { FeatureFlagsProvider } from './features/feature-flags/FeatureFlagsProvider';
import { initI18n } from './i18n';
import { initSentry } from './observability/sentry';
import { createQueryClient, setActiveQueryClient } from './query/client';
import { appRoutes } from './routes';
import './styles/globals.css';
// Registers every theme stylesheet (see src/theme/themes.ts).
import './theme/themes';
import { initTheme } from './theme/applyTheme';
import { initPointerLight } from './theme/pointerLight';

async function enableMocking(authMode: string): Promise<void> {
  // Gate on auth mode rather than import.meta.env.DEV so `vite build --mode development`
  // (our `pnpm build:dev` script) produces a working mock-auth artifact. The vite.config.ts
  // PTV2-910 guard refuses production builds with anything other than VITE_AUTH_MODE=real,
  // so MSW can never end up in a real prod artifact this way.
  if (authMode !== 'mock') return;
  const { worker } = await import('./test/msw/browser');
  await worker.start({
    onUnhandledRequest: 'bypass',
    serviceWorker: { url: '/mockServiceWorker.js' },
  });
}

async function bootstrap(): Promise<void> {
  // Apply the resolved theme before first paint to avoid a theme flash.
  initTheme();
  // Pointer-driven light source (used by the glass-dark theme).
  initPointerLight();
  const env = readEnv();
  // Start error tracking before anything else can throw. No-op unless a DSN is
  // configured (VITE_SENTRY_DSN). See src/observability/sentry.ts.
  initSentry(env);
  await initI18n();
  await enableMocking(env.authMode);

  const container = document.getElementById('root');
  if (container === null) {
    throw new Error('[new-ptv2] #root element is missing from index.html.');
  }

  const queryClient = createQueryClient();
  setActiveQueryClient(queryClient);
  const router = createBrowserRouter(appRoutes, {
    basename: import.meta.env.BASE_URL,
  });

  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider mode={env.authMode}>
          <RootErrorBoundary>
            <SessionGuard>
              {/* Inside SessionGuard (needs the session) and above the router:
                  blocks first paint until the flag map resolves (D26). */}
              <FeatureFlagsProvider>
                <RouterProvider router={router} />
              </FeatureFlagsProvider>
            </SessionGuard>
          </RootErrorBoundary>
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

bootstrap().catch((err: unknown) => {
  console.error('[new-ptv2] bootstrap failed', err);
});
